/**
 * React hook for native network status detection
 * Shows a banner when the user loses connection
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

export function useNativeNetwork() {
  const [isConnected, setIsConnected] = useState(true);
  const [connectionType, setConnectionType] = useState('unknown');

  useEffect(() => {
    let listener = null;

    const init = async () => {
      // Try to use Capacitor Network plugin first
      if (Capacitor.isPluginAvailable('Network')) {
        try {
          const { Network } = await import('@capacitor/network');
          const status = await Network.getStatus();
          setIsConnected(status.connected);
          setConnectionType(status.connectionType);
          listener = await Network.addListener('networkStatusChange', (status) => {
            setIsConnected(status.connected);
            setConnectionType(status.connectionType);
          });
          return;
        } catch (e) {
          console.warn('[Network] Plugin failed, using fallback:', e.message);
        }
      }
      
      // Fallback: use browser events
      // Default to true if navigator.onLine is undefined
      setIsConnected(navigator.onLine !== false);
      const goOnline = () => setIsConnected(true);
      const goOffline = () => setIsConnected(false);
      window.addEventListener('online', goOnline);
      window.addEventListener('offline', goOffline);
      listener = {
        remove: () => {
          window.removeEventListener('online', goOnline);
          window.removeEventListener('offline', goOffline);
        }
      };
    };

    init();

    return () => {
      if (listener?.remove) listener.remove();
    };
  }, []);

  return { isConnected, connectionType };
}
